import Controller from "sap/ui/core/mvc/Controller";
import UIComponent from "sap/ui/core/UIComponent";
import AppComponent from "../Component";
import Model from "sap/ui/model/Model";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import Router from "sap/ui/core/routing/Router";
import History from "sap/ui/core/routing/History";
import JSONModel from "sap/ui/model/json/JSONModel";
import type Event from "sap/ui/base/Event";
import type Control from "sap/ui/core/Control";

export interface PageHeaderState {
	title: string;
	subtitle: string;
}

/** Value state of each required field of the shared InviteUserDialog fragment (`view>/inviteValueState`). */
export interface InviteValueState {
	org: "None" | "Error";
	email: "None" | "Error";
	firstName: "None" | "Error";
	lastName: "None" | "Error";
}

export const INVITE_VALUE_STATE_NONE: InviteValueState = { org: "None", email: "None", firstName: "None", lastName: "None" };

const INVITE_FIELD_BY_CONTROL: Record<string, keyof InviteValueState> = {
	ddInviteOrg: "org",
	inpInviteEmail: "email",
	inpInviteFirstName: "firstName",
	inpInviteLastName: "lastName"
};

/**
 * @namespace computec.b2b.portal.controller
 */
export default abstract class BaseController extends Controller {
	/**
	 * Convenience method for accessing the component of the controller's view.
	 * @returns The component of the controller's view
	 */
	public getOwnerComponent(): AppComponent {
		return super.getOwnerComponent() as AppComponent;
	}

	/**
	 * Convenience method to get the components' router instance.
	 * @returns The router instance
	 */
	public getRouter(): Router {
		return UIComponent.getRouterFor(this);
	}

	/**
	 * Convenience method for getting the i18n resource bundle of the component.
	 * @returns {Promise<sap.base.i18n.ResourceBundle>} The i18n resource bundle of the component
	 */
	public getResourceBundle(): Promise<ResourceBundle> {
		const oModel = this.getOwnerComponent().getModel("i18n") as ResourceModel;
		return oModel.getResourceBundle() as Promise<ResourceBundle>;
	}

	/**
	 * Convenience method for getting the view model by name in every controller of the application.
	 * @param [sName] The model name
	 * @returns The model instance
	 */
	public getModel(sName?: string): Model {
		return this.getView().getModel(sName);
	}

	/**
	 * Convenience method for setting the view model in every controller of the application.
	 * @param oModel The model instance
	 * @param [sName] The model name
	 * @returns The current base controller instance
	 */
	public setModel(oModel: Model, sName?: string): BaseController {
		this.getView().setModel(oModel, sName);
		return this;
	}

	/**
	 * Convenience method for triggering the navigation to a specific target.
	 * @public
	 * @param sName Target name
	 * @param [oParameters] Navigation parameters
	 * @param [bReplace] Defines if the hash should be replaced (no browser history entry) or set (browser history entry)
	 */
	public navTo(sName: string, oParameters?: object, bReplace?: boolean): void {
		this.getRouter().navTo(sName, oParameters, undefined, bReplace);
	}

	/**
	 * Opens an organization's detail page. A partner's menu has neither Organizations nor Partners; its
	 * Customers list holds every child organization, sub-Partners included (spec 29 D-131). So its own
	 * organization opens as My company and any other one as a customer detail - the back button then
	 * leads to a list that holds the record.
	 */
	public navToOrganization(id: string): void {
		if (this.getOwnerComponent().getPermissions()?.organizationType() === "Partner") {
			const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
			if (id === userModel?.getProperty("/orgId")) {
				this.navTo("myCompany");
			} else {
				this.navTo("customerDetail", { id });
			}
			return;
		}
		this.navTo("organizationDetail", { id });
	}

	/**
	 * Convenience event handler for navigating back.
	 * It there is a history entry we go one step back in the browser history
	 * If not, it will replace the current entry of the browser history with the main route.
	 */
	public onNavBack(): void {
		const sPreviousHash = History.getInstance().getPreviousHash();
		if (sPreviousHash !== undefined) {
			window.history.go(-1);
		} else {
			this.getRouter().navTo("main", {}, undefined, true);
		}
	}

	/**
	 * Clears the error state of the invite-dialog field the user has just changed. Lives here because the
	 * InviteUserDialog fragment is shared by PortalUsers, OrganizationDetail and DeploymentDetail, and a
	 * handler missing from the loading controller makes Fragment.load fail.
	 */
	public onInviteFieldChange(event: Event<object, Control>): void {
		const localId = this.getView().getLocalId(event.getSource().getId()) ?? "";
		const field = INVITE_FIELD_BY_CONTROL[localId];
		if (field) {
			(this.getView().getModel("view") as JSONModel | undefined)?.setProperty(`/inviteValueState/${field}`, "None");
		}
	}

	/**
	 * Sets the page header state (title + subtitle) on a JSONModel under the name `header`.
	 * Used together with `view/fragments/PageHeader.fragment.xml` which binds to `{header>/title}` and `{header>/subtitle}`.
	 */
	public setPageHeader(state: PageHeaderState): void {
		const existing = this.getView().getModel("header") as JSONModel | undefined;
		if (existing) {
			existing.setData(state);
			return;
		}
		this.getView().setModel(new JSONModel(state), "header");
	}
}
